/**
 * S12, the query runner. Advanced only, and nearly free, because the grid it
 * renders results into is the grid S11 already built.
 *
 * WHY IT EXISTS AT ALL in a product built for somebody who has never used a
 * cloud: it is the single strongest credibility surface in the console, and
 * the cost of it is a textarea. A person who wants a console gets one. A
 * person who does not never sees it, because it is Advanced and it appends
 * below the grid rather than sitting beside it.
 *
 * THE PROMPT GLYPH LIVES HERE, and in exactly one other place in the entire
 * product: inside the logo tile. Here it is literal, because a person is
 * typing at a database and the glyph says which side of the machine they are
 * on. It is never a card eyebrow, never a header, never an empty state. That
 * single restriction is most of the line between a quiet sibling and a
 * reskin of the school's terminal costume.
 *
 * THE PROTOTYPE ADMITS IT IS A PROTOTYPE. Three queries are recognized and
 * answered out of the seeded rows, so their counts agree with the grid above
 * them. Everything else says plainly that there is no canned result for it.
 * A fake query engine would be a lie told on the one screen whose entire job
 * is to be believed.
 *
 * THE DESTRUCTIVE GUARD is the one piece of judgement in the file: a delete,
 * an update or a drop with no where clause touches every row, so it stops and
 * says which table, in the same sentence, before it runs.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router';

import { Button } from '../../components/primitives/Button';
import { ConnectionFields } from '../../components/patterns/ConnectionFields';
import { DataGrid } from '../../components/patterns/DataGrid';
import { whenLabel } from '../../components/patterns/DeployRow';
import { EmptyState } from '../../components/patterns/EmptyState';
import { Notice } from '../../components/patterns/Notice';
import { SectionPanel } from '../../components/patterns/SectionPanel';
import { Skeleton } from '../../components/patterns/Skeleton';

import { getProject, listRows, listTables } from '../../lib/api';
import { toCsv, toSqlCreate, toSqlInserts, downloadText } from '../../lib/export';
import { count } from '../../lib/format';
import { useAdvanced } from '../../lib/hooks/use-advanced';
import { useAsync } from '../../lib/hooks/use-async';
import { useProjectSlug } from '../../lib/hooks/use-project-slug';
import { t } from '../../lib/i18n/t';
import { cannedResult, CANNED_QUERIES, connectionFor, QUERY_HISTORY } from '../../lib/mock/seed';
import { useDemoStore } from '../../lib/mock/store';
import { captureEvent } from '../../lib/analytics/posthog';
import type { DbColumn, DbRow, QueryResult, SavedQuery } from '../../lib/types';

import '../../styles/screens/database.css';

/** Mac says Command, everything else says Control, and neither says a glyph. */
const ON_MAC =
  typeof navigator !== 'undefined' && /mac(intosh| os)/i.test(navigator.userAgent);

/**
 * The table a statement would rewrite every row of, or null when the
 * statement is safe or narrowed by a where clause.
 */
export function everyRowTarget(sql: string): string | null {
  const text = sql.toLowerCase();
  if (!/\b(delete|update|drop|truncate)\b/.test(text)) return null;
  if (/\bwhere\b/.test(text)) return null;
  const found = text.match(/\b(?:from|update|table|truncate)\s+([a-z_][a-z0-9_.]*)/);
  return found ? found[1] : null;
}

/** Result columns typed from the table they came out of, when we know it. */
function resultColumns(
  result: QueryResult,
  known: Map<string, DbColumn[]>,
): DbColumn[] {
  const source = result.sourceTable ? known.get(result.sourceTable) : undefined;
  return result.columns.map((name) => {
    const found = source?.find((column) => column.name === name);
    if (found) return found;
    const sample = result.rows[0]?.cells[name];
    const type = typeof sample === 'number' ? 'number' : 'text';
    return { name, type, nullable: true, primaryKey: false };
  });
}

/* ----------------------------------------------------------- the panel */

export interface QueryPanelProps {
  slug: string;
  /**
   * False when the screen's one ink block belongs to something else, which is
   * every time this panel is embedded under S11's grid.
   */
  ink?: boolean;
}

export function QueryPanel({ slug, ink = true }: QueryPanelProps) {
  const scenarioId = useDemoStore((s) => s.scenarioId);

  const { data, isLoading } = useAsync(async () => {
    const project = await getProject(slug);
    if (!project) return null;
    const tables = await listTables(project.id);
    const loaded = await Promise.all(
      tables.map(async (one) => [one.name, await listRows(project.id, one.name)] as const),
    );
    return { project, tables, rows: new Map(loaded) };
  }, [slug, scenarioId]);

  const [sql, setSql] = useState(CANNED_QUERIES[0]);
  const [result, setResult] = useState<QueryResult | null>(null);
  const [unanswered, setUnanswered] = useState(false);
  const [pending, setPending] = useState<string | null>(null);
  const [ran, setRan] = useState<SavedQuery[]>([]);

  useEffect(() => {
    setResult(null);
    setUnanswered(false);
    setPending(null);
    setRan([]);
  }, [slug, scenarioId]);

  const known = useMemo(() => {
    const map = new Map<string, DbColumn[]>();
    for (const table of data?.tables ?? []) map.set(table.name, table.columns);
    return map;
  }, [data]);

  if (isLoading) {
    return <Skeleton shape="panel" count={1} label={t('Loading your database')} />;
  }

  if (!data) return null;

  const { project, rows } = data;

  function run(confirmed = false) {
    const target = everyRowTarget(sql);
    if (target && !confirmed) {
      setPending(target);
      return;
    }
    setPending(null);

    const answer = cannedResult(sql, (name) => rows.get(name) ?? []);
    captureEvent('query_runner_executed', {
      project_slug: project.slug,
      query_length: sql.length,
      has_result: answer !== null,
      row_count: answer?.rows.length ?? 0,
    });
    setResult(answer);
    setUnanswered(answer === null);
    setRan((was) => [
      { id: `q_${Date.now()}`, sql: sql.trim(), at: new Date().toISOString() },
      ...was,
    ]);
  }

  function exportResult(kind: 'csv' | 'sql') {
    if (!result) return;
    captureEvent('query_runner_export_clicked', {
      project_slug: project.slug,
      format: kind,
    });
    const stem = `${project.slug}-query`;

    if (kind === 'csv') {
      downloadText(`${stem}.csv`, toCsv(result.columns, result.rows), 'text/csv');
      return;
    }

    const source = result.sourceTable;
    const columns = source ? known.get(source) : undefined;
    const wholeTable =
      source !== null &&
      columns !== undefined &&
      result.columns.every((name) => columns.some((column) => column.name === name));

    const text = wholeTable
      ? `${toSqlInserts(source as string, result.columns, result.rows)}\n`
      : `${toSqlCreate('query_result', resultColumns(result, known))}\n${toSqlInserts(
          'query_result',
          result.columns,
          result.rows,
        )}\n`;

    downloadText(`${stem}.sql`, text, 'application/sql');
  }

  const history: SavedQuery[] = [...ran, ...(QUERY_HISTORY[project.slug] ?? [])];
  const columns = result ? resultColumns(result, known) : [];
  const resultRows: DbRow[] = result?.rows ?? [];

  return (
    <div className="qr">
      <SectionPanel
        title={t('Run a query')}
        note={t('This prototype answers three example queries. Anything else says so.')}
      >
        <div className="qr__fence">
          {/* The literal prompt. One of exactly two places it is allowed. */}
          <span className="qr__prompt" aria-hidden="true">
            {'> '}
          </span>
          <textarea
            className="qr__input"
            value={sql}
            spellCheck={false}
            aria-label={t('Your query')}
            onChange={(event) => setSql(event.target.value)}
            onKeyDown={(event) => {
              if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
                event.preventDefault();
                run();
              }
            }}
          />
        </div>

        <div className="qr__run">
          <Button variant={ink ? 'solid' : 'outline'} onClick={() => run()}>
            {t('Run')}
          </Button>
          <span className="qr__hint">
            {ON_MAC
              ? t('Command and Enter runs it.')
              : t('Control and Enter runs it.')}
          </span>
        </div>

        {/* The three that work, offered rather than guessed at. A prototype
            that admits its limits should also show a person where they are. */}
        <div className="qr__examples">
          <span className="qr__examples-label">{t('EXAMPLES')}</span>
          {CANNED_QUERIES.map((example) => (
            <Button key={example} variant="ghost" size="sm" onClick={() => setSql(example)}>
              {example}
            </Button>
          ))}
        </div>

        {pending ? (
          <Notice
            tone="attention"
            title={t('This changes every row in {table}.', { table: pending })}
            actions={
              <>
                <Button variant="ghost" onClick={() => setPending(null)}>
                  {t('Cancel')}
                </Button>
                <Button variant="danger-outline" onClick={() => run(true)}>
                  {t('Run it anyway')}
                </Button>
              </>
            }
          >
            <p>{t('There is no where clause, so every row is included.')}</p>
          </Notice>
        ) : null}

        {unanswered ? (
          <Notice tone="quiet">
            <p>{t("We don't have a canned result for that query in this prototype.")}</p>
          </Notice>
        ) : null}

        {result ? (
          <>
            <div className="qr__result-head">
              <span className="qr__meta">
                {result.rows.length === 1
                  ? t('1 row in {ms} ms', { ms: result.ms })
                  : t('{n} rows in {ms} ms', { n: count(result.rows.length), ms: result.ms })}
              </span>
              <span className="qr__result-actions">
                <Button variant="outline" size="sm" onClick={() => exportResult('csv')}>
                  {t('CSV')}
                </Button>
                <Button variant="outline" size="sm" onClick={() => exportResult('sql')}>
                  {t('SQL')}
                </Button>
              </span>
            </div>

            <DataGrid
              label={t('Query results')}
              columns={columns}
              rows={resultRows}
              readOnly
              bleed
              empty={
                /* The grid keeps its header, so the columns the query asked
                   for are still on screen. The sentence says the query ran
                   and found nothing, which is a different fact from a table
                   that has nothing in it, and the two must not read alike. */
                <EmptyState
                  mark="table"
                  sentence={t('That query came back with no rows.')}
                  context={t('It ran. Nothing in this table matched it.')}
                />
              }
            />
          </>
        ) : null}
      </SectionPanel>

      <SectionPanel title={t('History')} flush>
        {history.length === 0 ? (
          /* No action, on purpose. The thing to do about an empty history is
             to run a query, and the box that runs one is directly above this
             panel with the cursor already in it. A button here would point at
             something the person is already looking at. */
          <EmptyState mark="clock" sentence={t('Queries you run will show up here.')} />
        ) : (
          <ul className="qr__history">
            {history.map((one) => (
              <li className="qr__history-item" key={one.id}>
                <button
                  type="button"
                  className="qr__history-row"
                  onClick={() => setSql(one.sql)}
                >
                  <span className="qr__history-sql">{one.sql}</span>
                  <span className="qr__history-when">{whenLabel(one.at)}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </SectionPanel>

      <SectionPanel title={t('Connection details')}>
        <ConnectionFields connection={connectionFor(project)} region={project.region} />
      </SectionPanel>
    </div>
  );
}

/* ---------------------------------------------------------- the screen */

export default function QueryRunner() {
  const slug = useProjectSlug() ?? '';
  const [advanced, setAdvanced] = useAdvanced();
  const { search } = useLocation();
  const base = `/p/${encodeURIComponent(slug)}`;

  // Deep linking here in Simple is a real thing a reviewer will do, and an
  // empty page would be the wrong answer. The page says where the control is
  // and offers it, rather than pretending the route does not exist.
  if (!advanced) {
    return (
      <div className="page">
        <header className="page__head">
          <h1 className="tg__crumb">
            <Link className="tg__crumb-link" to={`${base}/database${search}`}>
              {t('Database')}
            </Link>
            <span className="tg__crumb-sep" aria-hidden="true">
              /
            </span>
            <span className="tg__crumb-name">{t('Run a query')}</span>
          </h1>
        </header>

        <Notice
          tone="quiet"
          title={t('Running a query is an Advanced detail.')}
          actions={
            <Button variant="solid" onClick={() => setAdvanced(true)}>
              {t('Turn on Advanced')}
            </Button>
          }
        >
          <p>{t('Advanced adds technical details. It never hides anything.')}</p>
        </Notice>
      </div>
    );
  }

  return (
    <div className="page">
      <header className="page__head">
        <h1 className="tg__crumb">
          <Link className="tg__crumb-link" to={`${base}/database`}>
            {t('Database')}
          </Link>
          <span className="tg__crumb-sep" aria-hidden="true">
            /
          </span>
          <span className="tg__crumb-name">{t('Run a query')}</span>
        </h1>
      </header>

      <QueryPanel slug={slug} />
    </div>
  );
}
